import { useEffect, useState, useCallback } from 'react';

import { Link, useLocation, useNavigate, useRouteLoaderData } from 'react-router';
import { Trans } from '@lingui/react/macro';
import { SignedIn, SignedOut, UserButton } from '@clerk/react-router';

import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';

import { Button } from '~/components/ui/button';
import { LanguageSwitcher } from '~/components/LanguageSwitcher';
import TryForFreeButton from './TryForFreeButton';

const Header = ({ topOffset = 0 }: { topOffset?: number }) => {
  // Get locale from root loader data
  const rootData = useRouteLoaderData("root") as { locale?: string } | undefined;
  const currentLocale = rootData?.locale || "en";
  const navigate = useNavigate();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(true); // Show initially
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { scrollY } = useScroll();

  // Get post-auth path from environment variable (defaults to /practice)
  const postAuthPath = typeof window !== 'undefined' 
    ? (window.ENV?.POST_AUTH_PATH || '/practice')
    : '/practice';
  
  // Debug logging
  // useEffect(() => {
  //   if (typeof window !== 'undefined') {
  //     console.log('[Header] window.ENV:', window.ENV);
  //     console.log('[Header] POST_AUTH_PATH:', window.ENV?.POST_AUTH_PATH);
  //     console.log('[Header] postAuthPath:', postAuthPath);
  //   }
  // }, [postAuthPath]);

  // Hide Practice button on these routes
  const hidePracticeButton = ['/practice', '/processing', '/analysis'].includes(location.pathname);

  useEffect(() => {
    const unsubscribe = scrollY.on('change', (latest) => {
      // Show header at top (0-50px) OR after scrolling past hero (100px+)
      const shouldShow = latest < 50 || latest > 100;
      const hasScrolled = latest > 100;
      
      setIsVisible(shouldShow);
      setIsScrolled(hasScrolled);
    });

    return () => unsubscribe();
  }, [scrollY]);

  // Control backdrop visibility
  useEffect(() => {
    const backdrop = document.getElementById('mobile-backdrop');
    if (backdrop) {
      if (isMobileMenuOpen) {
        backdrop.className = 'fixed top-[68px] left-0 right-0 bottom-0 bg-black/80 z-30 lg:hidden';
        backdrop.onclick = () => setIsMobileMenuOpen(false);
      } else {
        backdrop.className = 'hidden';
        backdrop.onclick = null;
      }
    }
  }, [isMobileMenuOpen]);

  const smoothScrollTo = useCallback((elementId: string) => {
    // Add a small delay to account for mobile menu closing
    setTimeout(() => {
      const element = document.getElementById(elementId);
      if (element) {
        // Get header height to offset scroll position
        const headerHeight = 68;
        const elementPosition = element.offsetTop - headerHeight;
        
        window.scrollTo({
          top: elementPosition,
          behavior: 'smooth'
        });
      } else {
        // If element not found, navigate to home page and then scroll
        navigate(`/#${elementId}`);
        
        // After navigation, try to scroll to the element
        setTimeout(() => {
          const newElement = document.getElementById(elementId);
          if (newElement) {
            const headerHeight = 68;
            const elementPosition = newElement.offsetTop - headerHeight;
            
            window.scrollTo({
              top: elementPosition,
              behavior: 'smooth'
            });
          }
        }, 500); // Wait for page to load
      }
    }, 200);
  }, [navigate]);

  return (
    <>
      {/* Spacer to prevent content from being hidden under fixed header */}
      {/* <div className="h-[62px] lg:h-[96px]" aria-hidden="true" /> */}
      
      <motion.header 
      className={`w-full h-[62px] lg:h-[96px] px-4 sm:px-6 lg:px-8 fixed z-50 flex items-center transition-all duration-300 text-base ${
        isScrolled 
          ? 'bg-white lg:backdrop-blur-md lg:bg-white/95 border-b border-gray-200/50 shadow-sm' 
          : 'bg-white lg:bg-transparent border-b border-transparent'
      }`}
      initial={{ opacity: 1, y: 0 }}
      animate={{ 
        opacity: isVisible ? 1 : 0,
        y: isVisible ? 0 : -100,
      }}
      transition={{ 
        duration: 0.3, 
        ease: [0.25, 0.46, 0.45, 0.94]
      }}
      style={{
        top: topOffset,
        pointerEvents: isVisible ? 'auto' : 'none'
      }}
    >
      {/* Content container with 1080px max-width */}
      <div className="max-w-[1080px] mx-auto w-full px-4 flex items-center justify-between relative z-50">
        <Link
          to="/"
          className="tracking-tight hover:opacity-80 transition-opacity"
        >
          <img
            src="/images/logo-wide-blue.png"
            alt="Pavone"
            className="h-8 lg:h-14 w-auto"
          />
        </Link>
        
        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-6">
           {/* <motion.button
            onClick={() => smoothScrollTo('how-it-works')}
            className="text-base font-medium hover:opacity-80 transition-opacity"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            How it works
          </motion.button>
          <motion.button
            onClick={() => smoothScrollTo('reviews')}
            className="text-base font-medium hover:opacity-80 transition-opacity"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Reviews
          </motion.button>*/}
          <motion.button
            onClick={() => smoothScrollTo('features')}
            className="text-base font-medium hover:opacity-80 transition-opacity"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Trans>Features</Trans>
          </motion.button>
          <motion.button
            onClick={() => smoothScrollTo('pricing')}
            className="text-base font-medium hover:opacity-80 transition-opacity"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Trans>Pricing</Trans>
          </motion.button>
          <motion.button
            onClick={() => smoothScrollTo('reviews')}
            className="text-base font-medium hover:opacity-80 transition-opacity"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Trans>Reviews</Trans>
          </motion.button>
          {/* <motion.button
            onClick={() => smoothScrollTo('privacy')}
            className="text-base font-medium hover:opacity-80 transition-opacity"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Trans>Privacy</Trans>
          </motion.button> */}

          {/* <LanguageSwitcher variant="header" currentLocale={currentLocale} /> */}

          <SignedOut>
            <TryForFreeButton size="md" text={<Trans>SIGN IN</Trans>} />
          </SignedOut>
          
          <SignedIn>
            <Link to={postAuthPath}>
              <Button 
                variant="default" 
                size="sm"
                className="px-6 py-3 h-12 text-base font-semibold rounded-2xl bg-gradient-to-r from-[#8B793D] via-[#8B793D]/80 to-[#8B793D] text-white shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300 relative overflow-hidden group"
              >
                {/* Animated background */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#8B793D]/60 via-[#8B793D]/40 to-[#8B793D]/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                {/* Shimmer effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                
                <span className="relative z-10"><Trans>DASHBOARD</Trans></span>
                
                {/* Pulse effect */}
                <div className="absolute inset-0 rounded-2xl bg-white/20 scale-0 group-hover:scale-100 transition-transform duration-300"></div>
              </Button>
            </Link>
            <UserButton afterSignOutUrl="/" />
          </SignedIn>
        </nav>

        {/* Mobile Menu Button and User Button */}
        <div className="flex lg:hidden items-center gap-3">
          <SignedIn>
            {!hidePracticeButton && (
              <Link
                to="/practice"
                className="inline-flex items-center px-4 py-2 h-9 text-sm font-semibold rounded-xl bg-gradient-to-r from-[#8B793D] via-[#8B793D]/80 to-[#8B793D] text-white shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden group"
              >
                {/* Animated background */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#8B793D]/60 via-[#8B793D]/40 to-[#8B793D]/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                {/* Shimmer effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                
                <span className="relative z-10"><Trans>PRACTICE</Trans></span>
                
                {/* Pulse effect */}
                <div className="absolute inset-0 rounded-xl bg-white/20 scale-0 group-hover:scale-100 transition-transform duration-300"></div>
              </Link>
            )}
            <UserButton afterSignOutUrl="/" />
          </SignedIn>
          <SignedOut>
            <TryForFreeButton size="sm" text={<Trans>SIGN IN</Trans>} />
          </SignedOut>
          <motion.button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 rounded-md hover:bg-gray-100 transition-colors"
            whileTap={{ scale: 0.95 }}
          >
            {isMobileMenuOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </motion.button>
        </div>
      </div>



      {/* Mobile Menu */}
      <motion.div
        className="lg:hidden absolute top-full left-0 right-0 bg-white backdrop-blur-md border-b border-gray-200/50 shadow-lg z-50"
        initial={{ opacity: 0, height: 0 }}
        animate={{ 
          opacity: isMobileMenuOpen ? 1 : 0,
          height: isMobileMenuOpen ? 'auto' : 0
        }}
        transition={{ duration: 0.2 }}
        style={{ 
          overflow: 'hidden',
          pointerEvents: isMobileMenuOpen ? 'auto' : 'none'
        }}
      >
        <div className="w-full px-4 py-4">
          <nav className="flex flex-col space-y-4 items-center text-center">
            {/* <motion.button
              onClick={(e) => {
                e.preventDefault();
                setIsMobileMenuOpen(false);
                smoothScrollTo('how-it-works');
              }}
              className="text-center text-base font-medium hover:opacity-80 transition-opacity py-2 w-full"
              whileTap={{ scale: 0.98 }}
            >
              How it works
            </motion.button>
            <motion.button
              onClick={(e) => {
                e.preventDefault();
                setIsMobileMenuOpen(false);
                smoothScrollTo('reviews');
              }}
              className="text-center text-base font-medium hover:opacity-80 transition-opacity py-2 w-full"
              whileTap={{ scale: 0.98 }}
            >
              Reviews
            </motion.button> */}
            <motion.button
              onClick={(e) => {
                e.preventDefault();
                setIsMobileMenuOpen(false);
                smoothScrollTo('features');
              }}
              className="text-center text-base font-medium hover:opacity-80 transition-opacity py-2 w-full"
              whileTap={{ scale: 0.98 }}
            >
              <Trans>Features</Trans>
            </motion.button>
            <motion.button
              onClick={(e) => {
                e.preventDefault();
                setIsMobileMenuOpen(false);
                smoothScrollTo('pricing');
              }}
              className="text-center text-base font-medium hover:opacity-80 transition-opacity py-2 w-full"
              whileTap={{ scale: 0.98 }}
            >
              <Trans>Pricing</Trans>
            </motion.button>
            <motion.button
              onClick={(e) => {
                e.preventDefault();
                setIsMobileMenuOpen(false);
                smoothScrollTo('reviews');
              }}
              className="text-center text-base font-medium hover:opacity-80 transition-opacity py-2 w-full"
              whileTap={{ scale: 0.98 }}
            >
              <Trans>Reviews</Trans>
            </motion.button>
            {/* <motion.button
              onClick={(e) => {
                e.preventDefault();
                setIsMobileMenuOpen(false);
                smoothScrollTo('privacy');
              }}
              className="text-center text-base font-medium hover:opacity-80 transition-opacity py-2 w-full"
              whileTap={{ scale: 0.98 }}
            >
              <Trans>Privacy</Trans>
            </motion.button> */}

            <div className="py-2">
              <LanguageSwitcher variant="header" currentLocale={currentLocale} />
            </div>

            <SignedOut>
              <div className="w-full mt-6" onClick={() => setIsMobileMenuOpen(false)}>
                <TryForFreeButton size="md" className="w-full" text={<Trans>SIGN IN</Trans>} />
              </div>
            </SignedOut>
            
            <SignedIn>
              <Link 
                to={postAuthPath}
                onClick={() => setIsMobileMenuOpen(false)}
                className="w-full mt-6"
              >
                <Button 
                  variant="default" 
                  size="default" 
                  className="w-full h-12 px-6 py-3 text-base font-semibold rounded-2xl bg-gradient-to-r from-[#8B793D] via-[#8B793D]/80 to-[#8B793D] text-white shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300 relative overflow-hidden group"
                >
                  {/* Animated background */}
                  <div className="absolute inset-0 bg-gradient-to-r from-[#8B793D]/60 via-[#8B793D]/40 to-[#8B793D]/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  
                  {/* Shimmer effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                  
                  <span className="relative z-10"><Trans>DASHBOARD</Trans></span>
                  
                  {/* Pulse effect */}
                  <div className="absolute inset-0 rounded-2xl bg-white/20 scale-0 group-hover:scale-100 transition-transform duration-300"></div>
                </Button>
              </Link>
            </SignedIn>
          </nav>
        </div>
      </motion.div>
    </motion.header>
    </>
  );
};

export default Header;
